import { useEffect, useState } from 'react';
import { SombrasDaFaixa, classeFaixaRolavel, useFaixaRolavel } from './ui/FaixaRolavel.js';

/**
 * Navegacao por ancoras de uma pagina longa. Fica presa logo abaixo do topo e
 * destaca a secao que esta na tela. No celular rola de lado, com degrade na
 * borda que tem mais secoes, e a secao atual vem para a vista.
 */
export function NavInterna({ secoes }: { secoes: readonly { id: string; rotulo: string }[] }) {
  const [atual, setAtual] = useState(secoes[0]?.id ?? '');
  const { ref, resta } = useFaixaRolavel<HTMLOListElement>(atual, '[aria-current]');

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entradas) => {
        const visivel = entradas.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visivel) setAtual(visivel.target.id);
      },
      // Conta como "atual" a secao que cruza a faixa logo abaixo do topo fixo.
      { rootMargin: '-120px 0px -60% 0px' },
    );
    for (const s of secoes) {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    }
    return () => obs.disconnect();
  }, [secoes]);

  return (
    <nav
      aria-label="Seções da ficha"
      className="sticky top-14 z-10 -mx-4 border-b border-borda bg-fundo/95 px-4 backdrop-blur lg:-mx-8 lg:px-8"
    >
      <ol ref={ref} className={classeFaixaRolavel}>
        {secoes.map((s) => {
          const ativa = s.id === atual;
          return (
            <li key={s.id} className="shrink-0">
              <a
                href={`#${s.id}`}
                aria-current={ativa ? 'location' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  setAtual(s.id);
                }}
                className={`-mb-px flex h-11 items-center border-b-2 px-3 text-sm font-medium whitespace-nowrap select-none transition-colors ${
                  ativa ? 'border-marca text-marca' : 'border-transparent text-apagado hover:text-texto'
                }`}
              >
                {s.rotulo}
              </a>
            </li>
          );
        })}
      </ol>
      <SombrasDaFaixa resta={resta} />
    </nav>
  );
}
